import { AlertTriangle, RefreshCw } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { formatDateTime, timeAgo } from "@/lib/format";
import { displayTimeZone } from "@/lib/time-zone";

import { cx } from "./cx";
import { Button } from "@/components/ui";

/** How often the "Updated 2 minutes ago" label is redrawn so it stays true. */
const TICK_MS = 30_000;
/** Data older than this gets a quiet "may be out of date" note. */
export const STALE_AFTER_MS = 15 * 60_000;
/** Pages that have no refresh rhythm of their own refetch this often while the tab is visible. */
export const AUTO_REFRESH_MS = 5 * 60_000;

/** The query fields a page's freshness is read from (any TanStack `useQuery` result has them). */
interface FreshnessSource {
  dataUpdatedAt: number;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * The freshness of a page made of several queries: the oldest of their update times (the page is only as
 * fresh as its stalest part), busy while any is fetching, and a refresh that refetches them all.
 */
export function freshnessOf(...queries: FreshnessSource[]): {
  updatedAt: number;
  refreshing: boolean;
  onRefresh: () => Promise<unknown>;
} {
  const loaded = queries.filter((q) => q.dataUpdatedAt > 0).map((q) => q.dataUpdatedAt);
  return {
    updatedAt: loaded.length ? Math.min(...loaded) : 0,
    refreshing: queries.some((q) => q.isFetching),
    onRefresh: () => Promise.all(queries.map((q) => q.refetch())),
  };
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);
  return now;
}

/**
 * "Updated 2 minutes ago · Refresh": when a page's data was last fetched. The time shows the full date,
 * time and zone in a tooltip on hover and on keyboard focus. Renders nothing until there is data.
 */
export function DataFreshness({
  updatedAt,
  onRefresh,
  refreshing = false,
  staleAfterMs = STALE_AFTER_MS,
  className,
}: {
  /** `dataUpdatedAt` of the page's query (ms since epoch); 0 means not loaded yet. */
  updatedAt: number;
  onRefresh: () => unknown;
  refreshing?: boolean;
  staleAfterMs?: number;
  className?: string;
}) {
  const ticked = useNow();
  const [tipOpen, setTipOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const tipId = useId();
  // While a list shows its previous data for a new filter (`keepPreviousData`), its update time reads 0
  // until the new data lands; keep showing the last one so the strip doesn't blink out.
  const [lastSeen, setLastSeen] = useState(updatedAt);
  if (updatedAt && updatedAt !== lastSeen) setLastSeen(updatedAt);
  const at = updatedAt || lastSeen;

  if (!at) return null;

  // Never "in 3 seconds" when the clock and the fetch disagree slightly.
  const now = Math.max(ticked, at);
  const iso = new Date(at).toISOString();
  const age = now - at;
  const label = age < 60_000 ? "Updated just now" : `Updated ${timeAgo(iso, now)}`;
  const full = `${formatDateTime(iso)} (${displayTimeZone()})`;
  const stale = age > staleAfterMs;

  async function refresh() {
    setAnnouncement("");
    await onRefresh();
    setAnnouncement("Data refreshed");
  }

  return (
    <div className={cx("flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-subtle", className)}>
      <span className="relative inline-flex">
        {/* A button so keyboard users can reach the tooltip (and touch users can tap it open). */}
        <Button
          unstyled
          type="button"
          aria-describedby={tipId}
          onClick={() => setTipOpen((open) => !open)}
          onMouseEnter={() => setTipOpen(true)}
          onMouseLeave={() => setTipOpen(false)}
          onFocus={() => setTipOpen(true)}
          onBlur={() => setTipOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setTipOpen(false);
          }}
          className="cursor-default rounded underline decoration-dotted underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <time dateTime={iso}>{label}</time>
        </Button>
        <span
          id={tipId}
          role="tooltip"
          className={cx(
            "pointer-events-none absolute top-full left-0 z-20 mt-1 rounded-md bg-chrome-raised px-2 py-1 text-xs whitespace-nowrap text-white shadow",
            !tipOpen && "sr-only",
          )}
        >
          {full}
        </span>
      </span>
      {stale && (
        <span className="inline-flex items-center gap-1 text-amber-700" role="status">
          <AlertTriangle className="size-3.5" aria-hidden />
          May be out of date
        </span>
      )}
      <Button
        unstyled
        type="button"
        onClick={() => void refresh()}
        disabled={refreshing}
        aria-busy={refreshing}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold text-fg-muted hover:bg-muted hover:text-fg disabled:cursor-not-allowed disabled:opacity-60 print:hidden"
      >
        <RefreshCw className={cx("size-3.5", refreshing && "animate-spin")} aria-hidden />
        Refresh
      </Button>
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
